import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { formatApiErrorDetail } from "../lib/api";

const FEATURES_PT = [
  "Prompt injection e instruções direcionadas a IA",
  "Texto invisível e caracteres ocultos",
  "Anomalias de fonte, cor e metadados",
  "Relatório com hash SHA-256",
];
const FEATURES_EN = [
  "Prompt injection and AI-directed instructions",
  "Invisible text and hidden characters",
  "Font, color and metadata anomalies",
  "Report with SHA-256 hash",
];

export default function Login() {
  const { login, register } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const features = lang === "pt" ? FEATURES_PT : FEATURES_EN;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-navy text-white p-14 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute -right-24 top-1/4 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle,rgba(180,146,44,0.18),transparent 70%)" }} />
        <div className="relative">
          <Logo variant="dark" size="lg" />
        </div>
        <div className="relative">
          <p className="label-caps" style={{ color: "#D4AF37" }}>
            {t("login.tag")}
          </p>
          <h1 className="font-display font-bold text-4xl xl:text-5xl leading-tight mt-4">
            {t("dash.title")}
          </h1>
          <ul className="mt-8 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-slate-300">
                <ShieldCheck size={20} weight="fill" color="#D4AF37" />
                <span className="text-sm">{f}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-slate-500">
          © {new Date().getFullYear()} LEXADM · Document Integrity
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12 bg-white">
        <div className="lg:hidden mb-10">
          <Logo variant="light" />
        </div>
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-3xl text-navy">
              {mode === "login" ? t("login.welcome") : t("login.signup")}
            </h2>
            <button
              onClick={() => setLang(lang === "pt" ? "en" : "pt")}
              className="text-xs font-bold tracking-widest text-slate-400 hover:text-navy border border-slate-200 rounded px-2 py-1 transition-colors"
              data-testid="login-lang-toggle"
            >
              {lang === "pt" ? "EN" : "PT"}
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-2">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="label-caps">{t("login.name")}</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="input-name"
                  className="mt-1.5 w-full rounded-md border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition"
                />
              </div>
            )}
            <div>
              <label className="label-caps">{t("login.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
                className="mt-1.5 w-full rounded-md border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition"
              />
            </div>
            <div>
              <label className="label-caps">{t("login.password")}</label>
              <div className="relative mt-1.5">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="input-password"
                  className="w-full rounded-md border border-slate-200 px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy"
                  tabIndex={-1}
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-risk-critical bg-risk-critical/5 border border-risk-critical/20 rounded-md px-3 py-2"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-navy text-white font-semibold rounded-md py-3 hover:bg-black transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? t("common.loading") : mode === "login" ? t("login.signin") : t("login.signup")}
              {!loading && <ArrowRight size={18} weight="bold" />}
            </button>
          </form>

          <button
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            data-testid="toggle-mode"
            className="mt-6 text-sm text-slate-500 hover:text-navy transition-colors"
          >
            {mode === "login" ? t("login.toRegister") : t("login.toLogin")}
          </button>
        </div>
      </div>
    </div>
  );
}
